import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronRight, Clock3, Search, Ticket, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import API from "../services/api";

function Bids() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const starlineOnly = new URLSearchParams(location.search).get("type") === "starline";
  const [bids, setBids] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => getTodayDate());
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    API.get(`/bets/${user.id}`)
      .then((res) => setBids(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBids([]));
  }, [navigate, user]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = viewportWidth <= 480;
  const isCompact = viewportWidth <= 390;

  const visibleBids = useMemo(
    () => (starlineOnly ? bids.filter(isStarlineBid) : bids),
    [bids, starlineOnly]
  );
  const groups = useMemo(() => groupBids(visibleBids), [visibleBids]);
  const dateScopedGroups = groups.filter((group) => !selectedDate || group.dateKey === selectedDate);
  const filteredGroups = dateScopedGroups.filter((group) => {
    const text = `${group.marketName} ${group.baseType} ${group.session} ${group.numbers.join(" ")} ${group.total} ${group.winTotal}`.toLowerCase();
    const matchesStatus = statusFilter === "all" || group.status === statusFilter;
    return matchesStatus && text.includes(query.toLowerCase());
  });
  const summary = {
    all: dateScopedGroups.length,
    pending: dateScopedGroups.filter((group) => group.status === "pending").length,
    won: dateScopedGroups.filter((group) => group.status === "won").length,
    lost: dateScopedGroups.filter((group) => group.status === "lost").length,
  };

  return (
    <div style={page}>
      <header style={{ ...topbar, gridTemplateColumns: isCompact ? "40px 1fr" : topbar.gridTemplateColumns, padding: isCompact ? "0 10px" : topbar.padding }}>
        <button type="button" style={backButton} onClick={() => navigate("/dashboard")}>
          {"<"}
        </button>
        <strong>{starlineOnly ? "Starline History" : "My Bids"}</strong>
        {!isCompact && <div style={dateButton}>
          <CalendarDays size={15} />
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={dateInput} />
        </div>}
      </header>

      <main style={{ ...content, padding: isMobile ? "10px 10px 14px" : content.padding }}>
        <div style={{ ...summaryStrip, gridTemplateColumns: isMobile ? "repeat(4, minmax(0, 1fr))" : summaryStrip.gridTemplateColumns, gap: isMobile ? 6 : summaryStrip.gap }}>
          {[
            { key: "all", label: "All" },
            { key: "pending", label: "Result Pending" },
            { key: "won", label: "Won" },
            { key: "lost", label: "Lost" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              style={{
                ...(statusFilter === item.key ? activeFilter : filterButton),
                ...(isMobile ? compactFilterButton : null),
              }}
              onClick={() => setStatusFilter(item.key)}
            >
              <span>{item.label}</span>
              <strong>{summary[item.key]}</strong>
            </button>
          ))}
        </div>

        <div style={{ ...searchBox, ...(isMobile ? compactSearchBox : null) }}>
          <Search size={16} color="#94a3b8" />
          <input
            style={{ ...input, ...(isMobile ? compactInput : null) }}
            placeholder={starlineOnly ? "Search starline bids..." : "Search market, amount..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div style={{ ...dateFilterRow, ...(isMobile ? compactDateFilterRow : null) }}>
          <span style={{ ...dateFilterLabel, ...(isMobile ? compactDateFilterLabel : null) }}>Showing bids for</span>
          <div style={dateActions}>
            <div style={{ ...dateFilterPill, ...(isMobile ? compactDateFilterPill : null) }}>
              <CalendarDays size={14} />
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ ...pillDateInput, ...(isMobile ? compactPillDateInput : null) }} />
            </div>
            {selectedDate ? (
              <button type="button" style={{ ...clearButton, ...(isMobile ? compactDateActionButton : null) }} onClick={() => setSelectedDate("")}>
                All Dates
              </button>
            ) : (
              <button type="button" style={{ ...todayButton, ...(isMobile ? compactDateActionButton : null) }} onClick={() => setSelectedDate(getTodayDate())}>
                Today
              </button>
            )}
          </div>
        </div>

        {!filteredGroups.length && (
          <section style={emptyCard}>
            <Ticket size={34} color="#cbd5e1" />
            <p>No bids found</p>
          </section>
        )}

        <div style={{ ...list, gap: isMobile ? "10px" : list.gap }}>
          {filteredGroups.map((group) => (
            <article key={group.key} style={{ ...card, ...(isMobile ? compactCard : null), padding: isCompact ? "12px 10px" : isMobile ? "13px 12px" : card.padding }} onClick={() => setSelectedGroup(group)}>
              <div style={{ ...cardTop, ...(isMobile ? compactCardTop : null), flexWrap: "wrap" }}>
                <div style={{ ...marketLine, minWidth: 0 }}>
                  <span style={{ ...marketIcon, ...(isMobile ? compactMarketIcon : null) }}><Ticket size={isMobile ? 14 : 16} /></span>
                  <div style={{ minWidth: 0 }}>
                    <strong style={{ ...marketName, fontSize: isMobile ? "clamp(13px, 4vw, 15px)" : marketName.fontSize, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{group.marketName}</strong>
                    <div style={{ ...timeLine, ...(isMobile ? compactTimeLine : null) }}><Clock3 size={12} /> {group.time}</div>
                    <div style={{ ...dateStampLine, ...(isMobile ? compactDateStampLine : null) }}>{group.dateTimeLabel}</div>
                  </div>
                </div>
                <span style={{ ...sessionBadge(group.session), ...(isMobile ? compactSessionBadge : null) }}>{group.session}</span>
              </div>

              <p style={{ ...numbersLine, ...(isMobile ? compactNumbersLine : null), wordBreak: "break-word" }}>
                {group.baseType}: <strong>{group.numbers.join(", ")}</strong>
              </p>

              <div style={{ ...cardBottom, ...(isMobile ? compactCardBottom : null) }}>
                <div style={cardBottomTotals}>
                  <span>Total: <strong>{formatMoney(group.total)}</strong></span>
                  {group.status === "won" && group.winTotal > 0 ? <span style={wonAmountStamp}>Won {formatMoney(group.winTotal)}</span> : null}
                </div>
                <ChevronRight size={18} color="#94a3b8" />
              </div>
            </article>
          ))}
        </div>

        {!!filteredGroups.length && (
          <section style={pager}>
            Page 1 of 1 ({filteredGroups.length} groups)
            <div style={pagerButtons}>
              <button type="button" style={pageButton} disabled>Previous</button>
              <button type="button" style={activePageButton}>1</button>
              <button type="button" style={pageButton} disabled>Next</button>
            </div>
          </section>
        )}
      </main>

      {selectedGroup && (
        <div style={detailOverlay} onClick={() => setSelectedGroup(null)}>
          <section style={{ ...detailSheet, padding: isCompact ? "14px 14px 24px" : detailSheet.padding }} onClick={(event) => event.stopPropagation()}>
            <div style={sheetHandle} />
            <div style={detailHeader}>
              <div style={marketLine}>
                <span style={marketIcon}><Ticket size={16} /></span>
                <div>
                  <strong style={marketName}>{selectedGroup.marketName}</strong>
                  <div style={timeLine}>{selectedGroup.baseType} - <Clock3 size={13} /> {selectedGroup.time}</div>
                </div>
              </div>
              <button type="button" style={closeButton} onClick={() => setSelectedGroup(null)}>
                <X size={20} />
              </button>
            </div>

            <div style={{ ...pillRow, flexWrap: "wrap" }}>
              <span style={statusBadge(selectedGroup.status)}>{getStatusLabel(selectedGroup.status)}</span>
              <span style={sessionBadge(selectedGroup.session)}>{selectedGroup.isStarline ? "Time" : "Session"}: {selectedGroup.session}</span>
            </div>

            <section style={selectedBox}>
              <span style={selectedTitle}>Selected Numbers</span>
              {selectedGroup.items.map((item) => (
                <div key={item.id} style={numberDetail}>
                  <span style={wideNumberChip}>{item.number}</span>
                  <div>
                    <div>Amount: <strong>{formatMoney(item.amount)}</strong></div>
                    <span style={miniStatus(item.status)}>{getStatusLabel(item.status)}</span>
                    <div style={itemTimeStamp}>{formatBidDateTime(item.created_at || item.date || item.bet_date)}</div>
                  </div>
                </div>
              ))}
            </section>

            <div style={detailTotal}>
              <span>Total Amount</span>
              <strong>{formatMoney(selectedGroup.total)}</strong>
            </div>
            {selectedGroup.status === "won" && selectedGroup.winTotal > 0 ? (
              <div style={detailWinRow}>
                <span>Total Winning</span>
                <strong>{formatMoney(selectedGroup.winTotal)}</strong>
              </div>
            ) : null}
            <div style={bidIdLine}>Bid ID <strong>#{selectedGroup.items.map((item) => item.id).join(", #")}</strong></div>
          </section>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function groupBids(bids) {
  const map = new Map();
  (Array.isArray(bids) ? bids : []).forEach((bid) => {
    const type = parseBetType(bid.type);
    const starline = isStarlineBid(bid);
    const slotLabel = String(bid.time_slot || "").trim();
    const minuteKey = minuteBucket(bid.created_at || bid.date || bid.bet_date);
    const key = [
      bid.market_id || bid.market_name || "market",
      type.baseType,
      starline ? slotLabel || "slot" : type.session,
      minuteKey,
      String(bid.status || "pending").toLowerCase(),
    ].join("|");

    if (!map.has(key)) {
      map.set(key, {
        key,
        isStarline: starline,
        marketName: bid.market_name || "Market",
        baseType: starline ? `STARLINE ${type.baseType}` : type.baseType,
        session: starline ? (slotLabel || "STARLINE") : type.session,
        status: String(bid.status || "pending").toLowerCase(),
        time: starline ? (slotLabel || formatBidTime(bid.created_at || bid.date)) : formatBidTime(bid.created_at || bid.date),
        total: 0,
        winTotal: 0,
        numbers: [],
        items: [],
        dateKey: toDateKey(bid.created_at || bid.date || bid.bet_date),
        dateTimeLabel: starline
          ? `${formatBidDateTime(bid.created_at || bid.date || bid.bet_date)}${slotLabel ? ` • Result time ${slotLabel}` : ""}`
          : formatBidDateTime(bid.created_at || bid.date || bid.bet_date),
      });
    }

    const group = map.get(key);
    group.total += Number(bid.amount || 0);
    group.winTotal += Number(bid.payout || 0);
    group.numbers.push(String(bid.number));
    group.items.push(bid);
  });

  return Array.from(map.values()).map((group) => ({
    ...group,
    numbers: sortNumbers(group.numbers),
    items: group.items.sort((a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true })),
  }));
}

function isStarlineBid(bid) {
  const text = `${bid.type || ""} ${bid.market_type || ""} ${bid.market_name || ""} ${bid.time_slot || ""}`.toLowerCase();
  return text.includes("starline") || !!bid.time_slot;
}

function parseBetType(value = "") {
  const raw = String(value || "").toLowerCase();
  const session = raw.includes("open") ? "OPEN" : raw.includes("close") ? "CLOSE" : "OPEN";
  const base = raw
    .replace(/[-_](open|close)$/i, "")
    .replace(/[-_]/g, " ")
    .trim();
  return { baseType: prettyType(base || raw || "bet").toUpperCase(), session };
}

function sortNumbers(values) {
  return [...values].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

function minuteBucket(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "no-time";
  date.setSeconds(0, 0);
  return date.toISOString();
}

function prettyType(value = "") {
  return String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatBidTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function formatBidDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(value) {
  return `₹${Number(value || 0).toFixed(0)}`;
}

function getStatusLabel(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "pending") return "Bet Success - Result Pending";
  if (value === "won") return "Won";
  if (value === "lost") return "Lost";
  return prettyType(value);
}

function toDateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

const page = { minHeight: "100vh", background: "#eef3f9", paddingBottom: "74px" };
const topbar = { width: "min(900px, 100%)", height: "56px", margin: "0 auto", display: "grid", gridTemplateColumns: "40px 1fr auto", alignItems: "center", background: "#fff", padding: "0 14px", boxSizing: "border-box" };
const backButton = { border: 0, background: "transparent", fontSize: "22px", color: "#334155" };
const dateButton = { height: "40px", display: "flex", alignItems: "center", gap: "7px", border: 0, borderRadius: "10px", background: "#f8fafc", color: "#334155", padding: "0 12px", fontWeight: 700 };
const content = { width: "min(900px, 100%)", margin: "0 auto", padding: "12px 20px 16px", boxSizing: "border-box" };
const summaryStrip = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, margin: "2px 0 10px" };
const filterButton = { minHeight: 50, border: "1px solid #dbe3ef", background: "#fff", borderRadius: 12, display: "grid", alignContent: "center", justifyItems: "center", gap: 4, color: "#475569", fontWeight: 800 };
const activeFilter = { ...filterButton, background: "#2563eb", borderColor: "#2563eb", color: "#fff" };
const compactFilterButton = { minHeight: 42, borderRadius: 10, fontSize: 11, gap: 2 };
const dateFilterRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "0 0 12px", flexWrap: "wrap" };
const dateFilterLabel = { color: "#64748b", fontSize: 13, fontWeight: 700 };
const compactDateFilterRow = { gap: 8, marginBottom: 10 };
const compactDateFilterLabel = { fontSize: 11 };
const dateActions = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const dateFilterPill = { display: "inline-flex", alignItems: "center", gap: 8, minHeight: 38, borderRadius: 999, border: "1px solid #dbe3ef", background: "#fff", color: "#1e3a8a", padding: "0 12px", fontWeight: 800 };
const clearButton = { minHeight: 38, borderRadius: 999, border: "1px solid #dbe3ef", background: "#fff", color: "#475569", padding: "0 12px", fontWeight: 700, cursor: "pointer" };
const todayButton = { minHeight: 38, borderRadius: 999, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", padding: "0 12px", fontWeight: 700, cursor: "pointer" };
const dateInput = { border: 0, outline: 0, background: "transparent", color: "#334155", fontSize: 12, minWidth: 112 };
const pillDateInput = { border: 0, outline: 0, background: "transparent", color: "#1e3a8a", fontSize: 14, fontWeight: 700, minWidth: 120 };
const compactDateFilterPill = { minHeight: 32, padding: "0 9px", gap: 5, fontSize: 11 };
const compactPillDateInput = { minWidth: 104, fontSize: 12 };
const compactDateActionButton = { minHeight: 32, padding: "0 10px", fontSize: 11 };
const searchBox = { height: "42px", display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #dbe3ef", borderRadius: "12px", padding: "0 12px", margin: "0 0 26px" };
const input = { flex: 1, border: 0, outline: 0, background: "transparent", fontSize: 15 };
const compactSearchBox = { height: 38, borderRadius: 10, margin: "0 0 10px", padding: "0 10px" };
const compactInput = { fontSize: 13 };
const list = { display: "grid", gap: "18px" };
const card = { background: "#fff", border: "1px solid #dbe3ef", borderRadius: "14px", padding: "20px 18px 16px", cursor: "pointer", boxShadow: "0 6px 18px rgba(15,23,42,0.04)" };
const cardTop = { display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "14px" };
const marketLine = { display: "flex", alignItems: "center", gap: 12 };
const marketIcon = { width: 34, height: 34, borderRadius: 10, background: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center" };
const marketName = { fontSize: 18, fontWeight: 900, color: "#0f172a" };
const timeLine = { marginTop: 4, display: "flex", alignItems: "center", gap: 5, color: "#64748b", fontSize: 12 };
const dateStampLine = { marginTop: 4, color: "#94a3b8", fontSize: 12, fontWeight: 600 };
const numbersLine = { color: "#475569", margin: "0 0 16px", fontSize: 15 };
const cardBottom = { borderTop: "1px solid #eef2f7", paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", color: "#334155" };
const cardBottomTotals = { display: "grid", gap: 6 };
const wonAmountStamp = { display: "inline-flex", alignItems: "center", width: "fit-content", minHeight: 24, padding: "0 10px", borderRadius: 999, background: "linear-gradient(135deg,#dcfce7,#bbf7d0)", color: "#166534", border: "1px solid #86efac", fontSize: 11, fontWeight: 900 };
const compactCard = { borderRadius: 12, boxShadow: "0 4px 12px rgba(15,23,42,0.035)" };
const compactCardTop = { gap: 8, marginBottom: 9 };
const compactMarketIcon = { width: 30, height: 30, borderRadius: 9 };
const compactTimeLine = { marginTop: 3, fontSize: 11, gap: 4 };
const compactDateStampLine = { marginTop: 2, fontSize: 10 };
const compactSessionBadge = { padding: "5px 9px", fontSize: 10 };
const compactNumbersLine = { margin: "0 0 10px", fontSize: "clamp(12px, 3.4vw, 13px)", lineHeight: 1.35 };
const compactCardBottom = { paddingTop: 9, fontSize: 12 };
const emptyCard = { height: "124px", display: "grid", placeItems: "center", alignContent: "center", gap: "12px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "10px", color: "#334155" };
const pager = { marginTop: 18, background: "#fff", border: "1px solid #dbe3ef", borderRadius: 14, padding: 18, color: "#334155" };
const pagerButtons = { display: "flex", gap: 8, marginTop: 16 };
const pageButton = { height: 40, padding: "0 16px", border: "1px solid #dbe3ef", borderRadius: 8, background: "#fff", color: "#94a3b8", fontWeight: 700 };
const activePageButton = { ...pageButton, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };
const sessionBadge = (session) => {
  const isTimeSlot = /\d{1,2}:\d{2}\s*(AM|PM)/i.test(String(session || ""));
  return {
    padding: "7px 14px",
    borderRadius: "999px",
    background: isTimeSlot ? "#eff6ff" : session === "CLOSE" ? "#fee2e2" : "#dcfce7",
    color: isTimeSlot ? "#2563eb" : session === "CLOSE" ? "#dc2626" : "#15803d",
    fontSize: 12,
    fontWeight: 900,
    height: "fit-content"
  };
};
const detailOverlay = { position: "fixed", inset: 0, zIndex: 80, background: "rgba(15,23,42,0.58)", display: "flex", alignItems: "flex-end", justifyContent: "center" };
const detailSheet = { width: "min(428px, 100%)", maxHeight: "80vh", overflowY: "auto", background: "#fff", borderRadius: "22px 22px 0 0", padding: "14px 22px 28px", boxSizing: "border-box" };
const sheetHandle = { width: 44, height: 5, borderRadius: 999, background: "#cbd5e1", margin: "0 auto 20px" };
const detailHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 };
const closeButton = { width: 36, height: 36, border: 0, borderRadius: "50%", background: "#f1f5f9", color: "#64748b", display: "grid", placeItems: "center" };
const pillRow = { display: "flex", gap: 10, marginBottom: 16 };
const statusBadge = (status) => {
  const value = String(status || "pending").toLowerCase();
  return {
    padding: "9px 14px",
    borderRadius: 999,
    border: `1px solid ${value === "won" ? "#bbf7d0" : value === "lost" ? "#fecaca" : "#fde68a"}`,
    background: value === "won" ? "#dcfce7" : value === "lost" ? "#fee2e2" : "#fef9c3",
    color: value === "won" ? "#15803d" : value === "lost" ? "#dc2626" : "#b45309",
    fontWeight: 900,
    fontSize: 12,
  };
};
const selectedBox = { background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: 14, display: "grid", gap: 10 };
const selectedTitle = { color: "#2563eb", fontSize: 12, fontWeight: 800 };
const numberDetail = { background: "#fff", border: "1px solid #dbeafe", borderRadius: 9, padding: 10, display: "flex", alignItems: "center", gap: 12 };
const wideNumberChip = { minWidth: 56, maxWidth: 140, minHeight: 38, borderRadius: 9, display: "grid", placeItems: "center", padding: "0 10px", background: "#2563eb", color: "#fff", fontSize: 14, fontWeight: 900, whiteSpace: "nowrap" };
const miniStatus = (status) => ({ display: "inline-block", marginTop: 5, padding: "4px 7px", borderRadius: 6, background: "#fef3c7", color: "#92400e", fontSize: 10, fontWeight: 800 });
const itemTimeStamp = { marginTop: 6, color: "#64748b", fontSize: 12, fontWeight: 600 };
const detailTotal = { borderTop: "1px solid #e5e7eb", marginTop: 18, paddingTop: 14, display: "flex", justifyContent: "space-between", color: "#334155", fontSize: 18 };
const detailWinRow = { marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "10px 12px", color: "#166534", fontSize: 15, fontWeight: 900 };
const bidIdLine = { marginTop: 18, display: "flex", justifyContent: "space-between", color: "#475569", fontSize: 12 };

export default Bids;
